import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaGraduationCap, FaBriefcase, FaCalendarAlt, FaUsers, FaHandshake,
  FaChalkboardTeacher, FaBook, FaDollarSign, FaGlobe, FaLaptop,
  FaNewspaper, FaPodcast, FaMedal, FaChartLine, FaComments,
  FaUserTie, FaCode, FaRocket, FaHeart, FaLinkedin, FaEnvelope, FaPhone, FaQuoteLeft, FaBars
} from 'react-icons/fa';

const FeatureCard = ({ icon: Icon, title, description }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className="bg-white p-6 rounded-lg shadow-lg"
  >
    <Icon className="text-4xl text-blue-500 mb-4" />
    <h3 className="text-xl font-semibold mb-2">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

const LandingPage = () => {
  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="bg-blue-600 text-white py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <motion.h1
            initial={{ y: -50 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 120 }}
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4"
          >
            Welcome to Your Alumni Network
          </motion.h1>
          <motion.p
            initial={{ y: 50 }}
            animate={{ y: 0 }}
            transition={{ delay: 0.4, type: 'spring', stiffness: 120 }}
            className="text-xl sm:text-2xl mb-8"
          >
            Connect, Grow, and Thrive with Your Fellow Graduates
          </motion.p>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.6, type: 'spring', stiffness: 120 }}
          >
            <Link
              to="/signup"
              className="bg-white text-blue-600 py-3 px-6 rounded-full font-semibold text-lg hover:bg-blue-100 transition duration-300"
            >
              Join Now
            </Link>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">What We Offer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={FaUsers}
              title="Alumni Directory"
              description="Connect with classmates and expand your professional network."
            />
            <FeatureCard
              icon={FaBriefcase}
              title="Job Board"
              description="Explore career opportunities exclusively for our alumni."
            />
            <FeatureCard
              icon={FaCalendarAlt}
              title="Events & Reunions"
              description="Stay connected with alumni events and class reunions."
            />
            <FeatureCard
              icon={FaHandshake}
              title="Mentorship Program"
              description="Give back by mentoring current students or fellow alumni."
            />
            <FeatureCard
              icon={FaChalkboardTeacher}
              title="Continuing Education"
              description="Access exclusive courses and workshops for lifelong learning."
            />
            <FeatureCard
              icon={FaDollarSign}
              title="Fundraising Campaigns"
              description="Support your alma mater through various initiatives."
            />
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="bg-gray-800 text-white py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Our Impact</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            <div>
              <FaGraduationCap className="text-5xl mb-4 mx-auto" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="text-4xl font-bold">50,000+</span>
                <p className="text-xl">Alumni</p>
              </motion.div>
            </div>
            <div>
              <FaGlobe className="text-5xl mb-4 mx-auto" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <span className="text-4xl font-bold">100+</span>
                <p className="text-xl">Countries</p>
              </motion.div>
            </div>
            <div>
              <FaHandshake className="text-5xl mb-4 mx-auto" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <span className="text-4xl font-bold">5,000+</span>
                <p className="text-xl">Mentorships</p>
              </motion.div>
            </div>
            <div>
              <FaDollarSign className="text-5xl mb-4 mx-auto" />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <span className="text-4xl font-bold">$10M+</span>
                <p className="text-xl">Raised</p>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Latest News & Resources */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Stay Informed & Engaged</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div whileHover={{ scale: 1.05 }} className="bg-white p-6 rounded-lg shadow-lg">
              <FaNewspaper className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Alumni Newsletter</h3>
              <p className="text-gray-600">Get monthly updates on alumni achievements and university news.</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="bg-white p-6 rounded-lg shadow-lg">
              <FaPodcast className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Alumni Podcast</h3>
              <p className="text-gray-600">Listen to inspiring stories from successful alumni.</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="bg-white p-6 rounded-lg shadow-lg">
              <FaBook className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Resource Library</h3>
              <p className="text-gray-600">Access exclusive e-books, webinars, and career resources.</p>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} className="bg-white p-6 rounded-lg shadow-lg">
              <FaLaptop className="text-4xl text-blue-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Virtual Events</h3>
              <p className="text-gray-600">Participate in online networking sessions and workshops.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="bg-blue-100 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">Alumni Success Stories</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-lg shadow-lg"
            >
              <FaMedal className="text-4xl text-yellow-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Sarah Johnson</h3>
              <p className="text-gray-600 mb-4">CEO of TechInnovate, Class of 2005</p>
              <p>"The alumni network helped me connect with my co-founder and launch our startup."</p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-lg shadow-lg"
            >
              <FaChartLine className="text-4xl text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Michael Chen</h3>
              <p className="text-gray-600 mb-4">Investment Banker, Class of 2010</p>
              <p>"The mentorship program guided me towards a successful career in finance."</p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-lg shadow-lg"
            >
              <FaRocket className="text-4xl text-red-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Emily Patel</h3>
              <p className="text-gray-600 mb-4">Aerospace Engineer, Class of 2015</p>
              <p>"Connecting with alumni in my field opened doors to amazing opportunities at NASA."</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
        className="bg-blue-600 text-white py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8">Ready to reconnect and grow?</h2>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 120 }}
          >
            <Link
              to="/signup"
              className="bg-white text-blue-600 py-3 px-8 rounded-full font-semibold text-lg hover:bg-blue-100 transition duration-300 inline-flex items-center"
            >
              <FaUserTie className="mr-2" />
              Join Our Alumni Network
            </Link>
          </motion.div>
          <p className="mt-4 text-lg">Discover the power of your alumni community today!</p>
        </div>
      </motion.section>

    

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">What Our Alumni Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-lg shadow-lg"
            >
              <FaQuoteLeft className="text-4xl text-blue-500 mb-4" />
              <p className="text-gray-600 mb-4">"The alumni network has been an invaluable resource for my career growth. I've made connections that have led to amazing opportunities."</p>
              <div className="flex items-center">
                <img src="https://via.placeholder.com/50" alt="Alumni" className="rounded-full mr-3" />
                <div>
                  <h4 className="font-semibold">John Doe</h4>
                  <p className="text-sm text-gray-500">Class of 2010</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-lg shadow-lg"
            >
              <FaQuoteLeft className="text-4xl text-blue-500 mb-4" />
              <p className="text-gray-600 mb-4">"I love attending the alumni events. It's a great way to stay connected with old friends and make new ones in my industry."</p>
              <div className="flex items-center">
                <img src="https://via.placeholder.com/50" alt="Alumni" className="rounded-full mr-3" />
                <div>
                  <h4 className="font-semibold">Jane Smith</h4>
                  <p className="text-sm text-gray-500">Class of 2015</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white p-6 rounded-lg shadow-lg"
            >
              <FaQuoteLeft className="text-4xl text-blue-500 mb-4" />
              <p className="text-gray-600 mb-4">"The mentorship program helped me transition into a new career field. I'm grateful for the guidance and support from fellow alumni."</p>
              <div className="flex items-center">
                <img src="https://via.placeholder.com/50" alt="Alumni" className="rounded-full mr-3" />
                <div>
                  <h4 className="font-semibold">Alex Johnson</h4>
                  <p className="text-sm text-gray-500">Class of 2018</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Floating Action Button */}
      <motion.div
        className="fixed bottom-8 right-8"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: 'spring', stiffness: 120 }}
      >
       
      </motion.div>
    </div>
  );
};

export default LandingPage;